import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function UploadExcel() {
  const [file, setFile] = useState(null);
  const [excelType, setExcelType] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [imports, setImports] = useState([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const navigate = useNavigate();

  // Load import history
  useEffect(() => {
    fetchImportHistory();
  }, []);

  const fetchImportHistory = async () => {
    setLoadingImports(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/excel-imports', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImports(response.data || []);
    } catch (err) {
      console.error('Failed to load import history:', err);
    }
    setLoadingImports(false);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!excelType) {
      setMessage('Pilih tipe upload terlebih dahulu: Media Online atau Newspaper');
      return;
    }
    if (!file) {
      setMessage('Pilih file Excel terlebih dahulu');
      return;
    }

    const formData = new FormData();
    formData.append('excel', file);
    formData.append('excelType', excelType);

    setLoading(true);
    try {
      const response = await axios.post('/api/excel/upload', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
      });

      const analyticsCount = response.data.mediaAnalyticsCount || 0;
      setMessage(`✓ ${response.data.message} | 📊 Analytics: ${analyticsCount} baris`);
      setFile(null);
      document.querySelector('input[type="file"]').value = '';
      await fetchImportHistory();
      // Don't auto-redirect, let user see result
      // setTimeout(() => navigate('/news'), 2000);
    } catch (err) {
      console.error('Upload error:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Upload gagal';
      const details = err.response?.data?.details ? ` (${err.response.data.details})` : '';
      setMessage(`✗ Error: ${errorMsg}${details}`);
    }
    setLoading(false);
  };

  const handleMigrateAnalytics = async (importId) => {
    try {
      setMessage('Migrating to MediaAnalytics...');
      const response = await axios.post(`/api/excel-to-media/${importId}`);
      setMessage(`✓ ${response.data.message}`);
      await fetchImportHistory(); // Refresh
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Migration failed';
      setMessage(`✗ Migration error: ${errorMsg}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Excel</h2>
        <p className="text-gray-600">Unggah file Excel untuk diimpor ke sistem</p>
      </div>

      {/* Upload Form */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
        <form onSubmit={handleSubmit} className="p-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipe Upload
            </label>
            <select
              value={excelType}
              onChange={(e) => setExcelType(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm mb-4"
            >
              <option value="">Pilih tipe upload...</option>
              <option value="mediaonline">Media Online</option>
              <option value="newspaper">Newspaper</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Excel (.xlsx, .xls)
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-indigo-500 transition">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-8l-3.172-3.172a4 4 0 00-5.656 0L28 20m0 0l-3.172-3.172a4 4 0 00-5.656 0l-10.172 10.172"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500">
                    <span>Pilih file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="sr-only"
                      required
                    />
                  </label>
                  <p className="pl-1">atau drag dan drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  {file ? `File terpilih: ${file.name}` : 'Maksimal 50MB'}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Tips: Jika dipilih Newspaper, data akan disimpan ke tabel newspaper khusus. Jika Media Online, data akan diteruskan ke MediaAnalytics.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !file || !excelType}
              className="flex-1 bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Mengunggah...
                </span>
              ) : (
                '📤 Upload Excel'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                document.querySelector('input[type="file"]').value = '';
                setMessage('');
              }}
              className="bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition"
            >
              Reset
            </button>
          </div>

          {message && (
            <div className={`mt-4 p-4 rounded-md text-sm flex items-start gap-3 ${
              message.includes('✓') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <span className="text-lg">{message.includes('✓') ? '✅' : '❌'}</span>
              <span>{message}</span>
            </div>
          )}
        </form>
      </div>

      {/* Import History */}
      {!loadingImports && imports.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">📋 Riwayat Upload Terakhir</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Nama File</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipe</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Baris</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Kolom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Analytics</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Pengguna</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tanggal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {imports.slice(0, 10).map((imp) => (
                  <tr key={imp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{imp.fileName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{imp.uploadType === 'NEWSPAPER' ? 'Newspaper' : 'Media Online'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{imp.rowCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{imp.columnCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 bg-green-50">
                      <span className="text-green-800 font-medium">{imp.mediaAnalyticsCount || '0'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{imp.uploadedBy}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(imp.uploadedAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {imp.uploadType === 'MEDIA_ONLINE' ? (
                        <button
                          onClick={() => handleMigrateAnalytics(imp.id)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                          title="Migrate to MediaAnalytics"
                        >
                          📊
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loadingImports && (
        <div className="text-center py-8 text-gray-500">
          <p>Memuat riwayat...</p>
        </div>
      )}
    </div>
  );
}

